import {createContext, memo, useCallback, use, useEffect, useMemo, useReducer, useState} from 'react'
import {Button, FormControl, Textarea, TextInput} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useUnifiedBatch} from '../../../stores/layout-selectors'
import {useManageBatchTelemetry} from './manage-batch-telemetry'
import styles from './ManageBatchCommitTab.module.css'

/**
 * Static default commit message used by the unified batch panel.
 *
 * Today's per-comment commit dialog (`ApplySuggestionDialog.tsx`) builds the
 * default dynamically from author logins via `getCommitMessage()`. Per design
 * for the unified batch panel, this is a single static string regardless of
 * authorship.
 */
export const DEFAULT_COMMIT_MESSAGE = 'Apply batched suggestions from code review'

const BASE_ERROR_MESSAGE = 'There was an error trying to commit changes'

type FormState = {
  commitMessage: string
  extendedDescription: string
}

type FormAction =
  | {type: 'CHANGE_COMMIT_MESSAGE'; payload: string}
  | {type: 'CHANGE_EXTENDED_DESCRIPTION'; payload: string}

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'CHANGE_COMMIT_MESSAGE':
      return {...state, commitMessage: action.payload}
    case 'CHANGE_EXTENDED_DESCRIPTION':
      return {...state, extendedDescription: action.payload}
    default:
      return state
  }
}

/**
 * Shared state between the tab body (form fields) and the primary CTA in the
 * panel's actions row. The two render in separate parts of `ManageBatchPanel`,
 * so the form values, submission status, and error message live in a context
 * provider that wraps both.
 */
type ManageBatchCommitContextValue = {
  formValues: FormState
  setCommitMessage: (value: string) => void
  setExtendedDescription: (value: string) => void
  isSubmitting: boolean
  errorMessage: string | undefined
  submit: () => void
  canSubmit: boolean
}

const ManageBatchCommitContext = createContext<ManageBatchCommitContextValue | undefined>(undefined)

function useManageBatchCommitContext(): ManageBatchCommitContextValue {
  const ctx = use(ManageBatchCommitContext)
  if (!ctx) {
    throw new Error('ManageBatchCommit components must be rendered inside <ManageBatchCommitProvider>.')
  }
  return ctx
}

/**
 * Provider for the Commit suggestions tab.
 *
 * Wraps both the body slot (`ManageBatchCommitTab`) and the footer primary
 * CTA slot (`ManageBatchCommitTabPrimaryCta`) so they can share form state
 * and the submit callback without prop drilling through `ManageBatchPanel`.
 *
 * The submit handler is intentionally a stub that emits telemetry and resolves
 * synchronously. Wiring the real commit path requires mapping each
 * `UnifiedBatchItem` back to a full `SuggestedChange` (with `authorLogin`,
 * `suggestion[]`, `commentId`, …) so it can be passed to
 * `useSubmitSuggestedChangesMutation` along with `headOid` from
 * `useComparisonInfo()`. That hydration is the responsibility of the
 * batched-comments data-layer follow-up and is intentionally out of scope
 * here.
 */
export const ManageBatchCommitProvider = memo(function ManageBatchCommitProviderImpl({
  children,
  onSubmitted,
}: {
  children: React.ReactNode
  /** Called after a successful commit so the panel can close itself. */
  onSubmitted?: () => void
}) {
  const {items} = useUnifiedBatch()
  const {onSubmit, onSubmitSuccess, onSubmitFailure} = useManageBatchTelemetry()
  const [formValues, dispatch] = useReducer(formReducer, {
    commitMessage: DEFAULT_COMMIT_MESSAGE,
    extendedDescription: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const setCommitMessage = useCallback((value: string) => dispatch({type: 'CHANGE_COMMIT_MESSAGE', payload: value}), [])
  const setExtendedDescription = useCallback(
    (value: string) => dispatch({type: 'CHANGE_EXTENDED_DESCRIPTION', payload: value}),
    [],
  )

  const canSubmit = items.length > 0 && formValues.commitMessage.trim().length > 0 && !isSubmitting

  const submit = useCallback(() => {
    if (!canSubmit) return

    onSubmit('commit')
    setIsSubmitting(true)
    setErrorMessage(undefined)

    try {
      onSubmitSuccess('commit')
      setIsSubmitting(false)
      onSubmitted?.()
    } catch (error) {
      onSubmitFailure('commit', error)
      setIsSubmitting(false)
      const friendly = error instanceof Error ? error.message : undefined
      setErrorMessage(friendly ? `${BASE_ERROR_MESSAGE}: ${friendly}` : `${BASE_ERROR_MESSAGE}.`)
    }
  }, [canSubmit, onSubmit, onSubmitSuccess, onSubmitFailure, onSubmitted])

  const value = useMemo<ManageBatchCommitContextValue>(
    () => ({
      formValues,
      setCommitMessage,
      setExtendedDescription,
      isSubmitting,
      errorMessage,
      submit,
      canSubmit,
    }),
    [formValues, setCommitMessage, setExtendedDescription, isSubmitting, errorMessage, submit, canSubmit],
  )

  return <ManageBatchCommitContext value={value}>{children}</ManageBatchCommitContext>
})

/**
 * Binds the `github:submit-form` command (⌘/Ctrl+Enter) to the Commit tab's
 * submit handler for its entire subtree, mirroring how `ApplySuggestionDialog`
 * wraps its form in `ScopedCommands` so the batch can be committed via the
 * keyboard while focus is anywhere in the form area.
 *
 * `ScopedCommands` renders a `display: contents` wrapper when no `className`
 * or `as` is supplied, so this does not affect the panel's flex layout.
 */
export const ManageBatchCommitFormCommands = memo(function ManageBatchCommitFormCommandsImpl({
  children,
}: {
  children: React.ReactNode
}) {
  const {submit} = useManageBatchCommitContext()
  return <ScopedCommands commands={{'github:submit-form': submit}}>{children}</ScopedCommands>
})

/**
 * Body content for the Commit suggestions tab.
 *
 * Renders the required `Commit message` text input and the optional
 * `Commit description` textarea. Mirrors the field layout of the per-comment
 * `ApplySuggestionDialog.tsx` so the unified panel feels consistent with the
 * legacy single-suggestion dialog.
 *
 * The submission error is rendered inline below the form via
 * `FormControl.Validation` — same pattern as `ApplySuggestionDialog.tsx`.
 */
export const ManageBatchCommitTab = memo(function ManageBatchCommitTabImpl() {
  const {formValues, setCommitMessage, setExtendedDescription, errorMessage} = useManageBatchCommitContext()

  useEffect(() => {
    if (errorMessage) {
      announce(errorMessage)
    }
  }, [errorMessage])

  return (
    <div className={styles.form} data-testid="manage-batch-commit-tab">
      <FormControl required>
        <FormControl.Label>Commit message</FormControl.Label>
        <TextInput
          block
          name="commit_message"
          data-testid="manage-batch-commit-message-input"
          value={formValues.commitMessage}
          onChange={event => setCommitMessage(event.target.value)}
        />
      </FormControl>
      <FormControl>
        <FormControl.Label>Commit description</FormControl.Label>
        <Textarea
          block
          rows={3}
          name="commit_description"
          data-testid="manage-batch-commit-description-input"
          value={formValues.extendedDescription}
          onChange={event => setExtendedDescription(event.target.value)}
        />
      </FormControl>
      {errorMessage && (
        // The error is announced to screen readers via `announce()` from
        // `@github-ui/aria-live` (see the effect above); the inline
        // `FormControl.Validation` provides the visible message.
        <FormControl.Validation variant="error" className={styles.errorRow} data-testid="manage-batch-commit-error">
          {errorMessage}
        </FormControl.Validation>
      )}
    </div>
  )
})

/**
 * Primary CTA for the Commit suggestions tab footer slot.
 *
 * Renders the green `Commit suggestions` button; disabled when the batch is
 * empty, when the commit message is blank, or while a submission is in
 * flight. Spinner mirrors the inline-spinner pattern used by the legacy
 * `ApplySuggestionDialog.tsx` `Commit changes` button.
 */
export const ManageBatchCommitTabPrimaryCta = memo(function ManageBatchCommitTabPrimaryCtaImpl() {
  const {submit, canSubmit, isSubmitting} = useManageBatchCommitContext()

  return (
    <Button
      variant="primary"
      onClick={submit}
      disabled={!canSubmit}
      loading={isSubmitting}
      data-testid="manage-batch-commit-cta"
    >
      Commit suggestions
    </Button>
  )
})
